"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import { Save, X } from 'lucide-react'
import { apiClient } from "../lib/apiClient"
import { useToast } from "../hooks/use-toast"

interface Template {
  id: number
  name: string
  templateText: string
  modifiedBy: number
  modifiedOn: string
}

interface AddEditTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialData?: Template | null
}

export function AddEditTemplateModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  initialData
}: AddEditTemplateModalProps) {
  const { toast } = useToast()
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    templateText: "",
  })

  const [errors, setErrors] = useState<{
    name?: string
    templateText?: string
  }>({})

  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          id: initialData.id || 0,
          name: initialData.name || "",
          templateText: initialData.templateText || "",
        })
      } else {
        // Reset form for new entry
        setFormData({
          id: 0,
          name: "",
          templateText: "",
        })
      }
      setErrors({})
    }
  }, [isOpen, initialData])

  const validate = (): boolean => {
    const newErrors: typeof errors = {}
    
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required"
    }
    
    if (!formData.templateText || formData.templateText.trim() === "") {
      newErrors.templateText = "Template text is required"
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isFormValid = (): boolean => {
    return (
      formData.name.trim() !== "" &&
      formData.templateText.trim() !== ""
    )
  }

  const handleSave = async () => {
    if (!validate()) {
      return
    }

    setIsSubmitting(true)
    try {
      // Get current user ID from localStorage (set during login)
      const currentUserId = localStorage.getItem("id") || sessionStorage.getItem("id")
      
      const payload = {
        id: formData.id,
        name: formData.name.trim(),
        templateText: formData.templateText.trim(),
        modUser: currentUserId ? parseInt(currentUserId) : 0
      }
      
      console.log("Saving template payload:", payload)

      const response = await apiClient.post("/Admin/SaveTemplates", payload)
      
      const message = response.data?.message || 
        (formData.id === 0 ? "Template added successfully" : "Template updated successfully")
      
      toast({
        title: "Success",
        description: message,
        duration: 3000,
        className: "border border-green-300",
      })

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error("Error saving template:", error)
      console.error("Error response:", error?.response?.data)
      
      const errorMessage = error?.response?.data?.message || 
        error?.response?.data?.errors 
          ? Object.values(error.response.data.errors).flat().join(', ')
          : error?.message || "Failed to save template"
      
      toast({
        title: "Error",
        description: errorMessage,
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl bg-white border border-gray-200 rounded-lg shadow-lg p-0 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6 flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold text-gray-900">
            {initialData ? "Edit Template" : "Add New Template"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-6 px-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter template name"
            />
            {errors.name && (
              <p className="text-xs text-red-600">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="templateText" className="text-sm font-medium text-gray-700">
              Template Text <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="templateText"
              value={formData.templateText}
              onChange={(e) => setFormData({ ...formData, templateText: e.target.value })}
              className="bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500 min-h-[200px] font-mono text-sm"
              placeholder="Enter template text (HTML supported)"
            />
            {errors.templateText && (
              <p className="text-xs text-red-600">{errors.templateText}</p>
            )}
            <p className="text-xs text-gray-500">
              You can use HTML tags and placeholders like {"{{UserName}}"}, {"{{LinkURL}}"}, {"{{TableData}}"}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="px-6 py-2.5 text-base font-medium rounded-md"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!isFormValid() || isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white px-6 py-2.5 text-base font-medium rounded-md shadow-sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

